/**
 * JAISIU-3657 — restore the chat-thread scroll position captured at
 * the moment the operator started an in-thread search.
 *
 * The search input lets the operator clear or press Esc to dismiss
 * the search. The expected behaviour, per the ticket acceptance
 * criteria, is that the chat returns to the exact scroll position
 * it had before the search began — even when the operator paged
 * through deep matches during the search, which may have caused
 * `loadOlderChatHistory` to prepend older batches to the live
 * transcript.
 *
 * ## Why an anchor (not a raw scrollTop)
 *
 * When the deep-batch walker paged in older rows, the DOM shifted:
 * the operator's "currently visible" message is now further down the
 * rendered window. A raw `scrollTop = N` would put the viewport
 * somewhere far from where the operator was reading. We instead
 * capture the topmost-visible-row's `data-msg-id` plus the
 * fractional position of the viewport center within that row, then
 * at restore time look up the row's CURRENT `offsetTop` and place
 * the viewport center at the same fractional position.
 *
 * `positionRatio` is a number in [0, 1]. The capture path
 * (`captureScrollAnchor` in `app-scroll.ts`) stores this alongside
 * the row's `messageId` and `viewportHeight` so the restore is
 * robust to layout shift between capture and restore (an image
 * finished loading, a long-message card collapsed, etc).
 *
 * ## Inputs / outputs
 *
 *   - `findThreadScrollContainer(host)` — locate the chat-thread
 *     scroll container. The chat view applies two classes
 *     (`.chat-thread-scroll` and `.chat-fleet-thread-scroll`) for
 *     back-compat with the older non-fleet layout; the selector
 *     mirrors the one `scrollToTop()` already uses.
 *   - `resolveThreadSearchRestoreTarget(anchor, container)` — pure
 *     function. Given a captured anchor and a container, returns
 *     the integer `scrollTop` to write, or `null` when the row is
 *     not in the DOM and the caller must first drain older pages.
 *   - `applyThreadSearchRestore(anchor, container)` — convenience
 *     wrapper around the resolver + a synchronous write of
 *     `container.scrollTop`. Caller is responsible for wrapping
 *     this in `withProgrammaticScroll(...)` so the resulting
 *     `scroll` event is NOT classified as a user gesture.
 *
 * The helpers are deliberately pure DOM so they can be unit-tested
 * in jsdom without a Lit renderRoot. The host (`app.ts`) wires the
 * capture path inside `handleChatMessageSearchQueryChange` (on the
 * empty→non-empty transition) and the restore path inside
 * `handleChatMessageSearchClear` (on Esc and on natural clear).
 */

/** A captured pre-search reading position for the chat-thread. */
export interface ChatThreadSearchAnchor {
  /** Stable id of the row at the viewport center at capture time. */
  messageId: string;
  /** Fractional position of the viewport center inside the row, [0, 1]. */
  positionRatio: number;
  /** `container.clientHeight` at capture time — used as a sanity check. */
  viewportHeight: number;
  /** Session key this anchor was captured for. Drops on session switch. */
  sessionKey: string;
  /** Diagnostic only — not used by restore math. */
  capturedAt: number;
}

const CHAT_THREAD_SCROLL_SELECTORS = [
  ".chat-fleet-thread-scroll",
  ".chat-thread-scroll",
] as const;

const MSG_ID_SELECTOR = "[data-msg-id]";

/**
 * Escape a `data-msg-id` attribute value for inclusion in a CSS
 * selector string. The IDs the chat controller assigns are normally
 * `[A-Za-z0-9_-]+` but we do not want a maliciously-crafted id to
 * break out of the selector — escape everything that could.
 */
export function escapeAttributeSelector(value: string): string {
  // The CSS attribute-selector escape is `\` + hex of the code point.
  return value.replace(/["\\]|\s/g, (ch) => {
    return "\\" + ch.charCodeAt(0).toString(16).padStart(2, "0") + " ";
  });
}

/**
 * Return the first element matching one of the chat-thread scroll
 * container selectors, or `null` if the chat view is not currently
 * mounted. Mirrors `scrollToTop()` in `app.ts`.
 */
export function findThreadScrollContainer(
  root: ParentNode | null | undefined,
): HTMLElement | null {
  if (!root) return null;
  for (const sel of CHAT_THREAD_SCROLL_SELECTORS) {
    const el = root.querySelector(sel) as HTMLElement | null;
    if (el) return el;
  }
  return null;
}

/**
 * Find the row for `messageId` inside `container`, or `null` when
 * the row is not currently rendered.
 */
export function findRowByMessageId(
  container: HTMLElement,
  messageId: string,
): HTMLElement | null {
  if (!messageId) return null;
  const sel = `[data-msg-id="${escapeAttributeSelector(messageId)}"]`;
  return container.querySelector(sel) as HTMLElement | null;
}

/**
 * Resolve a `scrollTop` value that, when written to `container`,
 * places the viewport center at the same fractional position inside
 * the captured row that it had at capture time.
 *
 * Returns `null` when the row is not in the DOM — the caller MUST
 * then drain older pages (`loadOlderChatHistory`) and re-call. We do
 * NOT return a "best effort" value here because a stale scrollTop
 * landing a few hundred pixels off the anchor is worse UX than a
 * short delay before the user sees the restored position.
 *
 * Edge cases handled:
 *   - row height zero (jsdom / unlaid-out DOM): we floor at 1 so
 *     the math does not divide by zero; the restored scrollTop may
 *     be slightly off but is still bounded.
 *   - viewport taller than the row: we floor the target at 0 so the
 *     viewport top does not push above the row top.
 *   - viewport taller than scrollHeight: floor target at 0.
 */
export function resolveThreadSearchRestoreTarget(
  anchor: ChatThreadSearchAnchor,
  container: HTMLElement,
): number | null {
  if (!anchor || !anchor.messageId) return null;
  const row = findRowByMessageId(container, anchor.messageId);
  if (!row) return null;
  const rowTop = row.offsetTop || 0;
  const rowHeight = Math.max(1, row.offsetHeight || 1);
  const viewportHeight = Math.max(0, container.clientHeight || 0);
  // The capture stored the fractional position of the viewport
  // CENTER within the row. Mirror that here so the viewport center
  // lands on the same proportional point.
  const safeRatio =
    typeof anchor.positionRatio === "number" &&
    Number.isFinite(anchor.positionRatio)
      ? Math.max(0, Math.min(1, anchor.positionRatio))
      : 0.5;
  const viewportCenterWithinRow = safeRatio * rowHeight;
  const targetCenter = rowTop + viewportCenterWithinRow;
  const target = Math.round(targetCenter - viewportHeight / 2);
  const maxScroll = Math.max(0, (container.scrollHeight || 0) - viewportHeight);
  return Math.max(0, Math.min(maxScroll, target));
}

/**
 * Convenience wrapper that resolves + writes `container.scrollTop`
 * SYNCHRONOUSLY. The caller is responsible for wrapping this in
 * `withProgrammaticScroll(host, ...)` so the resulting `scroll`
 * event is not classified as a user gesture.
 *
 * Returns `true` when a restore target was resolved and written;
 * `false` when the row is not in the DOM (caller should drain
 * older pages and retry).
 */
export function applyThreadSearchRestore(
  anchor: ChatThreadSearchAnchor,
  container: HTMLElement,
): boolean {
  const target = resolveThreadSearchRestoreTarget(anchor, container);
  if (target === null) return false;
  container.scrollTop = target;
  return true;
}

/**
 * Capture the current viewport center against the topmost
 * intersecting row in `container`. Mirrors the algorithm in
 * `captureScrollAnchor` (`app-scroll.ts`) but is read-only — the
 * caller stores the result wherever it likes (we use an ephemeral
 * in-memory field on the host, never persisted to IDB).
 *
 * Returns `null` when the container has no message rows.
 */
export function captureThreadSearchAnchor(
  container: HTMLElement,
  sessionKey: string,
): ChatThreadSearchAnchor | null {
  const key = (sessionKey ?? "").trim();
  if (!key) return null;
  const rows = container.querySelectorAll<HTMLElement>(MSG_ID_SELECTOR);
  if (rows.length === 0) return null;
  const scrollTop = container.scrollTop || 0;
  const clientHeight = container.clientHeight || 0;
  const viewportCenter = scrollTop + clientHeight / 2;
  // Pick the LAST row whose top is at-or-below the viewport center,
  // i.e. the topmost visible row. This mirrors the "last row whose
  // offsetTop <= scrollTop" rule used elsewhere in the scroll-anchor
  // system so the captured anchor is exactly the row the operator
  // is reading.
  let chosen: HTMLElement | null = null;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const top = row.offsetTop || 0;
    if (top <= viewportCenter) {
      chosen = row;
      continue;
    }
    break;
  }
  if (!chosen) chosen = rows[0];
  const messageId = chosen.getAttribute("data-msg-id");
  if (!messageId) return null;
  const rowHeight = Math.max(1, chosen.offsetHeight || 1);
  const positionRatio = Math.max(
    0,
    Math.min(1, (viewportCenter - (chosen.offsetTop || 0)) / rowHeight),
  );
  return {
    messageId,
    positionRatio,
    viewportHeight: clientHeight,
    sessionKey: key,
    capturedAt: Date.now(),
  };
}
